import FunnelActivityPrompt from "@/components/FunnelActivityPrompt";
import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { TorriLogo, TorriMark } from "@/components/TorriLogo";
import VestaBadge from "@/components/VestaBadge";
import ElectricVesta from "@/pages/vesta/HearthVesta";
import {
  LayoutDashboard, Users, Building2, FileBarChart, UserCog, LogOut, PanelLeftClose, PanelLeft,
  Handshake, GraduationCap, Filter, Gauge, UserRound, CalendarDays, SlidersHorizontal, Upload, Receipt, FileText, ClipboardList, Target, DatabaseBackup, Sparkles, BrainCircuit, Mail, Megaphone, BookUser, MapPin, Wrench, Award, Gift, Menu, X, Boxes, Calculator, Truck, FileSignature, PackageCheck, Wallet, KeyRound, ShieldAlert, CalendarRange, Warehouse, Activity, Bell, PlugZap, SunMedium, Tag,
} from "lucide-react";

const ROLE_LABELS = { admin: "Amministratore", agency: "Agenzia", partner: "Partner", commercial_manager: "Resp. Commerciale", back_office: "Back Office", head_of_partner: "Head of Partner", technical: "Area Tecnica" };
const GROUP_LABELS = { 1: "Dashboard", 11: "Agenzie", 2: "Gestionale", 20: "Magazzino", 3: "Calendari", 41: "Clienti", 42: "Prospect", 4: "Database", 7: "Beyond Partner", 45: "Tools", 46: "Ai VESTA", 5: "Impostazioni", 6: "Comunicazioni", 9: "Account" };
const BOXED_GROUPS = new Set([7, 45]);
const SUPERBOXES = [
  { id: "gestionale", label: "Gestionale", groups: new Set([2, 20]),
    box: "border-emerald-400/40 bg-emerald-400/[0.06]", lbl: "text-emerald-400", sub: "text-emerald-500/70" },
  { id: "azione", label: "Azione commerciale", groups: new Set([3, 41, 42]),
    box: "border-sky-400/40 bg-sky-400/[0.06]", lbl: "text-sky-400", sub: "text-sky-500/70" },
];
const superboxFor = (grp) => SUPERBOXES.find((s) => s.groups.has(grp));

function NavItem({ n, collapsed, onItemClick }) {
  if (n.disabled) {
    return (
      <div
        data-testid={n.testid}
        aria-disabled="true"
        className="flex cursor-not-allowed items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-muted-foreground/50"
        title="In arrivo"
      >
        <n.icon className="h-4 w-4 shrink-0" />
        {!collapsed && (
          <span className="flex flex-1 items-center justify-between gap-2">
            <span>{n.label}</span>
            {n.badge && (
              <span className="rounded-full bg-accent px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-muted-foreground" data-testid={`${n.testid}-badge`}>
                {n.badge}
              </span>
            )}
          </span>
        )}
      </div>
    );
  }
  return (
    <NavLink
      to={n.to}
      end={n.to === "/"}
      data-testid={n.testid}
      onClick={onItemClick}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
          isActive
            ? "bg-primary text-primary-foreground"
            : "text-muted-foreground hover:bg-accent hover:text-foreground"
        }`
      }
    >
      <n.icon className="h-4 w-4 shrink-0" />
      {!collapsed && <span>{n.label}</span>}
    </NavLink>
  );
}

function NavList({ nav, collapsed, onItemClick }) {
  const groups = [];
  nav.forEach((n) => {
    const last = groups[groups.length - 1];
    if (!last || last.group !== n.group) groups.push({ group: n.group, items: [n] });
    else last.items.push(n);
  });

  const renderItems = (g) => g.items.map((n) => <NavItem key={n.to || n.testid} n={n} collapsed={collapsed} onItemClick={onItemClick} />);

  const out = [];
  for (let gi = 0; gi < groups.length; gi++) {
    const g = groups[gi];
    if (collapsed) {
      out.push(
        <div key={g.group} className="space-y-1">
          {gi > 0 && <div className="my-2 border-t border-border" aria-hidden="true" />}
          {renderItems(g)}
        </div>
      );
      continue;
    }
    const sb = superboxFor(g.group);
    if (sb) {
      const inner = [];
      let j = gi;
      for (; j < groups.length && superboxFor(groups[j].group) === sb; j++) {
        const gg = groups[j];
        const showSub = GROUP_LABELS[gg.group] !== sb.label;
        inner.push(
          <div key={gg.group} className="space-y-1">
            {showSub && <div className={`px-2 pb-0.5 pt-1 text-[10px] font-bold uppercase tracking-widest ${sb.sub}`}>{GROUP_LABELS[gg.group]}</div>}
            {renderItems(gg)}
          </div>
        );
      }
      out.push(
        <div key={`${sb.id}-${gi}`} className={`relative !mt-8 !mb-4 space-y-2 rounded-lg border ${sb.box} p-2 pt-3`} data-testid={`nav-group-box-${sb.id}`}>
          <span className={`absolute -top-2.5 left-3 rounded bg-card px-1.5 text-[10px] font-bold uppercase tracking-widest ${sb.lbl}`} data-testid={`nav-group-${sb.id}`}>{sb.label}</span>
          {inner}
        </div>
      );
      gi = j - 1;
      continue;
    }
    if (BOXED_GROUPS.has(g.group)) {
      out.push(
        <div key={g.group} className="relative !mt-8 !mb-4 space-y-1 rounded-lg border border-primary/30 bg-primary/[0.04] p-2 pt-3" data-testid={`nav-group-box-${g.group}`}>
          <span className="absolute -top-2.5 left-3 rounded bg-card px-1.5 text-[10px] font-bold uppercase tracking-widest text-primary" data-testid={`nav-group-${g.group}`}>{GROUP_LABELS[g.group]}</span>
          {renderItems(g)}
        </div>
      );
      continue;
    }
    out.push(
      <div key={g.group} className="space-y-1">
        <div className={`px-3 pb-1 ${gi === 0 ? "pt-1" : "pt-4"} text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60`} data-testid={`nav-group-${g.group}`}>{GROUP_LABELS[g.group]}</div>
        {renderItems(g)}
      </div>
    );
  }
  return <>{out}</>;
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  GROUP_LABELS[11] = user?.role === "agency" ? "Agenzia" : "Agenzie";
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const nav = [
    { to: "/michele-torri", label: "Dashboard Michele Torri", icon: BrainCircuit, testid: "nav-michele", roles: ["admin"], group: 1 },
    { to: "/direzionale", label: "Valutazione Rete Vendita (Dashboard responsabile)", icon: Gauge, testid: "nav-direzionale", roles: ["admin", "commercial_manager", "head_of_partner"], group: 1 },
    { to: "/", label: "Analisi Vendite e Profili Clienti (Dashboard agenzie)", icon: LayoutDashboard, testid: "nav-dashboard", roles: ["admin", "agency", "partner"], group: 1 },
    { to: "/dashboard-direzione", label: "Analisi Vendite e Profili Clienti (Dashboard agenzie)", icon: LayoutDashboard, testid: "nav-dashboard-cm", roles: ["commercial_manager", "head_of_partner"], group: 1 },
    { to: "/backoffice", label: "Dashboard BackOffice", icon: LayoutDashboard, testid: "nav-backoffice", roles: ["admin", "back_office"], page: "backoffice", group: 1 },
    { to: "/operativita", label: "Valutazione agenzie", icon: Gauge, testid: "nav-operativita", roles: ["admin", "agency", "commercial_manager", "head_of_partner"], group: 11 },
    { to: "/obiettivi-agenzie", label: "Obiettivi agenzie", icon: Target, testid: "nav-obiettivi", roles: ["admin", "commercial_manager", "head_of_partner"], group: 11 },
    { to: "/agencies", label: user?.role === "agency" ? "Profilo agenzia" : "Profilo agenzie", icon: Building2, testid: "nav-agencies", roles: ["admin", "agency", "commercial_manager", "head_of_partner"], group: 11 },
    { to: "/parametri-profilazione", label: "Parametri Profilazione", icon: SlidersHorizontal, testid: "nav-parametri-profilazione", roles: ["admin"], group: 11 },
    { to: "/anagrafiche", label: "Anagrafiche", icon: BookUser, testid: "nav-anagrafiche", roles: ["admin", "commercial_manager", "agency", "head_of_partner", "technical"], group: 2 },
    { to: "/affidamenti", label: "Affidamenti", icon: Wallet, testid: "nav-affidamenti", roles: ["admin", "back_office", "commercial_manager", "head_of_partner", "agency"], group: 2 },
    { to: "/listino", label: "Listino Prodotti", icon: Boxes, testid: "nav-listino", roles: ["admin", "back_office", "commercial_manager", "head_of_partner", "agency", "technical"], group: 2 },
    { to: "/commesse", label: "Commesse in corso", icon: PackageCheck, testid: "nav-commesse", roles: ["admin", "commercial_manager", "head_of_partner", "back_office", "agency", "technical"], group: 2 },
    { to: "/ordini", label: "Ordini", icon: Receipt, testid: "nav-orders", roles: ["admin", "commercial_manager", "agency", "head_of_partner", "technical"], group: 2 },
    { to: "/preventivi", label: "Preventivi", icon: FileText, testid: "nav-quotes", roles: ["admin", "commercial_manager", "agency", "head_of_partner", "technical"], group: 2 },
    { to: "/preventivatore", label: "Preventivatore", icon: Calculator, testid: "nav-preventivatore", roles: ["admin", "back_office", "commercial_manager", "head_of_partner", "agency", "technical"], group: 2 },
    { to: "/ordinati-scoperti", label: "Articoli scoperti", icon: Boxes, testid: "nav-ordinati-scoperti", roles: ["admin", "back_office", "technical"], page: "backoffice", group: 20 },
    { to: "/approvvigionamenti", label: "Pianificazione", icon: CalendarRange, testid: "nav-approvvigionamenti", roles: ["admin", "back_office", "technical"], page: "backoffice", group: 20 },
    { to: "/stima-magazzino", label: "Stima magazzino", icon: Warehouse, testid: "nav-stima-magazzino", roles: ["admin", "back_office", "technical"], page: "backoffice", group: 20 },
    { to: "/agenda", label: "Calendario", icon: CalendarDays, testid: "nav-agenda", roles: ["admin", "commercial_manager", "agency", "head_of_partner", "back_office"], group: 3 },
    { to: "/attivita-agenzie", label: "Attività Agenzie", icon: Handshake, testid: "nav-agency-activities", roles: ["admin", "commercial_manager", "agency", "head_of_partner"], group: 3 },
    { to: "/attivita-responsabile", label: "Attività Responsabile", icon: UserRound, testid: "nav-manager-activities", roles: ["admin", "commercial_manager", "head_of_partner"], group: 3 },
    { to: "/clienti/mappa", label: "Mappa Clienti", icon: MapPin, testid: "nav-clienti-mappa", roles: ["admin", "agency", "partner", "commercial_manager", "head_of_partner"], group: 41 },
    { to: "/clienti/portafoglio", label: "Portafoglio", icon: Users, testid: "nav-clienti-portafoglio", roles: ["admin", "agency", "partner", "commercial_manager", "head_of_partner"], group: 41 },
    { to: "/prospect/mappa", label: "Mappa Prospect", icon: MapPin, testid: "nav-prospect-mappa", roles: ["admin", "commercial_manager", "agency", "head_of_partner"], group: 42 },
    { to: "/prospect/funnel", label: "Funnel", icon: Filter, testid: "nav-prospect-funnel", roles: ["admin", "commercial_manager", "agency", "head_of_partner"], group: 42 },
    { to: "/beyond/portafoglio-partner", label: "Portafoglio Partner", icon: Handshake, testid: "nav-portafoglio-partner", roles: ["admin", "back_office", "commercial_manager", "head_of_partner", "agency"], group: 7 },
    { to: "/beyond/punti-vesta", label: "Punti Vesta", icon: VestaBadge, testid: "nav-punti-vesta", roles: ["admin", "back_office", "commercial_manager", "head_of_partner", "agency"], group: 7 },
    { to: "/beyond/catalogo-vesta", label: "Catalogo Vesta", icon: Gift, testid: "nav-catalogo-vesta", roles: ["admin", "back_office", "commercial_manager", "head_of_partner", "agency"], group: 7 },
    { to: "/beyond/edit-catalogo", label: "Edit Catalogo", icon: SlidersHorizontal, testid: "nav-edit-catalogo", roles: ["admin", "head_of_partner"], group: 7 },
    { to: "/beyond/azioni-marketing", label: "Prodotti/Servizi prenotati", icon: Megaphone, testid: "nav-azioni-marketing", roles: ["admin", "back_office", "commercial_manager", "head_of_partner", "agency"], group: 7 },
    { to: "/programma", label: "Eventi Partner & Webinar", icon: GraduationCap, testid: "nav-programma", roles: ["admin", "back_office", "commercial_manager", "head_of_partner", "agency"], group: 7 },
    { to: "/pratiche-configuratore", label: "Consulenze commerciali", icon: ClipboardList, testid: "nav-pratiche-configuratore", roles: ["admin", "agency", "commercial_manager", "head_of_partner", "back_office", "technical"], group: 45 },
    { to: "/consulenze-tecniche", label: "Consulenze tecniche", icon: FileSignature, testid: "nav-consulenze-tecniche", roles: ["admin", "agency", "commercial_manager", "head_of_partner", "back_office", "technical"], group: 45 },
    { to: "/tools", label: "Tools", icon: Wrench, testid: "nav-tools", roles: ["admin", "agency", "partner", "commercial_manager", "head_of_partner", "back_office", "technical"], group: 45 },
    { to: "/parametri-dimensionamento", label: "Parametri dimensionamento", icon: SlidersHorizontal, testid: "nav-parametri-dimensionamento", roles: ["admin", "commercial_manager", "head_of_partner", "back_office", "technical"], group: 45 },
    { to: "/conversazioni-vesta", label: "Apprendimento Vesta", icon: BrainCircuit, testid: "nav-conversazioni-vesta", roles: ["admin", "head_of_partner"], group: 46 },
    { to: "/tariffario-vesta-pro", label: "Tariffario Vesta Pro", icon: Tag, testid: "nav-tariffario-vesta-pro", roles: ["admin"], group: 46 },
    { to: "/torri-monitoring", label: "Torri Monitoring", icon: Activity, testid: "nav-torri-monitoring", roles: ["admin", "head_of_partner", "back_office", "technical"], page: "monitoring", group: 46 },
    { to: "/ev-monitoring", label: "EV Monitoring", icon: PlugZap, testid: "nav-ev-monitoring", roles: ["admin", "head_of_partner", "back_office", "technical"], page: "monitoring", group: 46 },
    { to: "/tigo-monitoring", label: "Tigo Monitoring", icon: Sparkles, testid: "nav-tigo-monitoring", roles: ["admin", "head_of_partner", "back_office", "technical"], page: "monitoring", group: 46 },
    { to: "/import-dati", label: "Importa Dati", icon: Upload, testid: "nav-import-dati", roles: ["admin"], group: 4 },
    { to: "/mail-automatiche", label: "Mail Automatiche", icon: Mail, testid: "nav-mail", roles: ["admin"], group: 6 },
    { to: "/comunicazioni-vesta", label: "Comunicazioni Vesta", icon: Bell, testid: "nav-comunicazioni-vesta", roles: ["admin", "head_of_partner"], group: 6 },
    { to: "/newsletter", label: "Newsletter", icon: Megaphone, testid: "nav-newsletter", roles: ["admin", "head_of_partner"], group: 6 },
    { to: "/rubrica", label: "Rubrica contatti", icon: BookUser, testid: "nav-rubrica", roles: ["admin", "head_of_partner"], group: 6 },
    { to: "/impostazioni-note", label: "Qualità Note (AI)", icon: Sparkles, testid: "nav-note-quality", roles: ["admin"], group: 5 },
    { to: "/config-trasporto", label: "Config. Trasporto", icon: Truck, testid: "nav-config-trasporto", roles: ["admin"], group: 5 },
    { to: "/condizioni-contratto", label: "Condizioni Contrattuali", icon: FileSignature, testid: "nav-condizioni-contratto", roles: ["admin"], group: 5 },
    { to: "/users", label: "Utenti", icon: UserCog, testid: "nav-users", roles: ["admin"], group: 5 },
    { to: "/backup", label: "Backup & Salvataggi", icon: DatabaseBackup, testid: "nav-backup", roles: ["admin"], group: 5 },
    { to: "/cambia-password", label: "Cambia password", icon: KeyRound, testid: "nav-change-password", roles: ["admin", "agency", "partner", "commercial_manager", "head_of_partner", "back_office", "technical"], group: 9 },
  ].filter((n) => {
    const bo = user?.role === "back_office" || user?.role_actual === "back_office";
    if (n.to === "/import-dati") return user?.role === "admin" || bo;
    if (n.roles.includes(user?.role)) return true;
    if (bo && (n.roles.includes("back_office") || n.roles.includes("commercial_manager"))) return true;
    return !!(n.page && (user?.extra_pages || []).includes(n.page));
  });

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background text-foreground">
      <aside
        className={`${collapsed ? "w-20" : "w-64"} hidden md:flex flex-col border-r border-border bg-card transition-all duration-200`}
        data-testid="sidebar"
      >
        <div className="flex h-16 items-center gap-2 border-b border-border px-4">
          {collapsed
            ? <TorriMark className="mx-auto h-10 w-10" />
            : <TorriLogo imgClass="h-9" />}
        </div>

        <nav className="flex-1 min-h-0 space-y-1 overflow-y-auto p-3">
          <NavList nav={nav} collapsed={collapsed} />
        </nav>

        <div className="border-t border-border p-3">
          <button
            onClick={() => setCollapsed((c) => !c)}
            data-testid="toggle-sidebar"
            className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            {collapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
            {!collapsed && <span>Comprimi</span>}
          </button>
        </div>
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-[60] md:hidden" data-testid="mobile-nav">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-overlay" />
          <aside className="absolute left-0 top-0 flex h-full w-72 max-w-[82%] flex-col border-r border-border bg-card shadow-xl">
            <div className="flex h-16 items-center justify-between gap-2 border-b border-border px-4">
              <TorriLogo imgClass="h-9" />
              <Button variant="ghost" size="icon" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-close">
                <X className="h-5 w-5" />
              </Button>
            </div>
            <nav className="flex-1 min-h-0 space-y-1 overflow-y-auto p-3">
              <NavList nav={nav} collapsed={false} onItemClick={() => setMobileOpen(false)} />
            </nav>
            <div className="border-t border-border p-3">
              <button onClick={doLogout} className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground" data-testid="mobile-logout">
                <LogOut className="h-4 w-4" /> <span>Esci</span>
              </button>
            </div>
          </aside>
        </div>
      )}

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="z-50 flex h-16 shrink-0 items-center justify-between border-b border-border bg-card px-3 sm:px-6">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
              <Menu className="h-5 w-5" />
            </Button>
            <button type="button" onClick={() => navigate("/")} className="md:hidden" aria-label="Home" data-testid="mobile-header-logo">
              <TorriMark className="h-9 w-9" />
            </button>
            <div className="hidden truncate font-heading text-lg font-bold tracking-tight sm:block" data-testid="page-brand">
              Gestione Clienti &amp; Agenzie
            </div>
          </div>
          <div className="flex items-center gap-3">
            {["admin", "head_of_partner", "back_office", "commercial_manager", "technical"].includes(user?.role) && (
              <button type="button" onClick={() => navigate("/vesta/home")} title="Entra in Ai VESTA" data-testid="header-vesta-link"
                className="px-2 py-1.5 text-[11px] font-extrabold tracking-[0.2em] transition-transform duration-200 hover:scale-105 active:scale-95">
                <ElectricVesta />
              </button>
            )}
            <div className="hidden text-right sm:block">
              <div className="text-sm font-semibold leading-tight" data-testid="user-name">{user?.name}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                {ROLE_LABELS[user?.role] || user?.role}
              </div>
            </div>
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-accent font-heading text-sm font-bold">
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <Button variant="outline" size="icon" onClick={doLogout} data-testid="logout-btn">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </header>

        <main className="flex-1 overflow-auto p-6 lg:p-8" data-testid="main-content">{children}</main>
        <FunnelActivityPrompt />
      </div>
    </div>
  );
}
